// Key signatures: how many sharps/flats a key has, which letters they alter,
// and where each accidental sits on the treble staff. Drives both the drawn
// key signature and the accidental-display logic in the engraver. See SPEC §4.

import { parseKey } from '@/music/notes';
import { SMUFL, type SmuflGlyph } from './smufl';

// Circle-of-fifths position (signed accidental count) per major tonic.
const MAJOR_FIFTHS: Record<string, number> = {
  C: 0, G: 1, D: 2, A: 3, E: 4, B: 5, 'F#': 6, 'C#': 7,
  F: -1, Bb: -2, Eb: -3, Ab: -4, Db: -5, Gb: -6, Cb: -7,
};
// Minor tonics resolve through their relative major.
const MINOR_FIFTHS: Record<string, number> = {
  A: 0, E: 1, B: 2, 'F#': 3, 'C#': 4, 'G#': 5, 'D#': 6, 'A#': 7,
  D: -1, G: -2, C: -3, F: -4, Bb: -5, Eb: -6, Ab: -7,
};

// Order accidentals are added, and their staff position (yBelowTop) on treble.
const SHARP_ORDER = ['F', 'C', 'G', 'D', 'A', 'E', 'B'] as const;
const FLAT_ORDER = ['B', 'E', 'A', 'D', 'G', 'C', 'F'] as const;
const SHARP_Y: Record<string, number> = { F: 0, C: 1.5, G: -0.5, D: 1, A: 2.5, E: 0.5, B: 2 };
const FLAT_Y: Record<string, number> = { B: 2, E: 0.5, A: 2.5, D: 1, G: 3, C: 1.5, F: 3.5 };

/** Signed accidental count for a key label (+ sharps, − flats). 0 if unknown. */
export function keyFifths(key: string | undefined): number {
  const parsed = parseKey(key);
  if (!parsed) return 0;
  const table = parsed.mode === 'minor' ? MINOR_FIFTHS : MAJOR_FIFTHS;
  return table[parsed.tonic] ?? 0;
}

/** The accidentals to draw for a key, in order, with treble staff positions. */
export interface KeySigAccidental {
  glyph: SmuflGlyph;
  /** staff-spaces below the top line. */
  y: number;
}

export function keySignature(key: string | undefined): KeySigAccidental[] {
  const fifths = keyFifths(key);
  if (fifths === 0) return [];
  const sharp = fifths > 0;
  const order = sharp ? SHARP_ORDER : FLAT_ORDER;
  const yMap = sharp ? SHARP_Y : FLAT_Y;
  const glyph = sharp ? SMUFL.accidentalSharp : SMUFL.accidentalFlat;
  return order.slice(0, Math.abs(fifths)).map((letter) => ({ glyph, y: yMap[letter] }));
}

/** Map of letter -> alteration (+1 sharp, −1 flat) implied by the key. */
export function keyAlterations(key: string | undefined): Map<string, number> {
  const fifths = keyFifths(key);
  const map = new Map<string, number>();
  const order = fifths > 0 ? SHARP_ORDER : FLAT_ORDER;
  for (const letter of order.slice(0, Math.abs(fifths))) {
    map.set(letter, fifths > 0 ? 1 : -1);
  }
  return map;
}
